"use client"
import { useState, useEffect } from "react"
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { db } from "../config/firebase"
import { useAuth } from "../AuthContext"

const INTER_FONT = "'Inter', Arial, Helvetica, sans-serif"

const DEFAULTS = {
  email:      "info@agapeprimehotel.com",
  copyright:  "© Agape Prime Hotel, Inc. 2025–Present. All rights reserved.",
  address:    "XXX TBD Road, Belize City, Belize XXXXX",
  phoneFax:   "Phone: 1-877-732-3492 | Fax: 1-877-760-2763 / 1-718-771-5900",
  legalText:  "Copyright. Trademark. Agape Prime, LLC and/or assigns. 2025-present. All related indicia included. All rights reserved.",
  termsUrl:   "https://www.agapeprimehotel.com/WP/terms-of-service/",
  privacyUrl: "https://www.agapeprimehotel.com/WP/privacy-policy/",
}

export default function Footer({ sx = {} }) {
  const { isAdmin } = useAuth()
  const [content, setContent] = useState(DEFAULTS)
  const [editOpen, setEditOpen] = useState(false)
  const [draft, setDraft]     = useState(DEFAULTS)
  const [saving, setSaving]   = useState(false)
  const [saved, setSaved]     = useState(false)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const snap = await getDoc(doc(db, "siteContent", "footer"))
        if (snap.exists()) setContent({ ...DEFAULTS, ...snap.data() })
      } catch (err) {
        console.error("Failed to load footer:", err)
      }
    }
    fetchFooter()
  }, [])

  const handleEditOpen = () => {
    setDraft({ ...content })
    setSaved(false)
    setSaveError(null)
    setEditOpen(true)
  }

  const handleEditClose = () => setEditOpen(false)

  const handleChange = (field) => (e) =>
    setDraft((prev) => ({ ...prev, [field]: e.target.value }))

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      await setDoc(doc(db, "siteContent", "footer"), draft)
      setContent({ ...draft })
      setSaved(true)
      setTimeout(handleEditClose, 1200)
    } catch (err) {
      console.error("Failed to save footer:", err)
      setSaveError(err.code === "permission-denied"
        ? "Permission denied. Make sure the Firestore rules are deployed."
        : err.message || "Save failed. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Box
        component="footer"
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          padding: "2rem 1rem",
          textAlign: "center",
          fontFamily: INTER_FONT,
          fontSize: { xs: "0.85rem", sm: "1rem" },
          lineHeight: 1.6,
          position: "relative",
          ...sx,
        }}
      >
        {isAdmin && (
          <IconButton
            onClick={handleEditOpen}
            size="small"
            title="Edit footer"
            sx={{
              position: "absolute",
              top: 12,
              right: 16,
              backgroundColor: "rgba(255,255,255,0.15)",
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        <p>{content.email}</p>
        <p>{content.copyright}</p>
        <p>{content.address}</p>
        <p>{content.phoneFax}</p>
        <p style={{ fontSize: "0.8rem", opacity: 0.8 }}>
          {content.legalText}{" "}
          <a
            href={content.termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "white", textDecoration: "underline" }}
          >
            Terms of service
          </a>
          .{" "}
          <a
            href={content.privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "white", textDecoration: "underline" }}
          >
            Privacy policy
          </a>
          .
        </p>
      </Box>

      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: INTER_FONT, fontWeight: 600 }}>
          Edit Footer
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Email"
            value={draft.email}
            onChange={handleChange("email")}
            fullWidth size="small"
          />
          <TextField
            label="Copyright line"
            value={draft.copyright}
            onChange={handleChange("copyright")}
            fullWidth size="small"
          />
          <TextField
            label="Address"
            value={draft.address}
            onChange={handleChange("address")}
            fullWidth size="small"
          />
          <TextField
            label="Phone / Fax"
            value={draft.phoneFax}
            onChange={handleChange("phoneFax")}
            fullWidth size="small"
          />
          <TextField
            label="Legal text"
            value={draft.legalText}
            onChange={handleChange("legalText")}
            fullWidth size="small"
            multiline rows={3}
          />
          <TextField
            label="Terms of Service URL"
            value={draft.termsUrl}
            onChange={handleChange("termsUrl")}
            fullWidth size="small"
          />
          <TextField
            label="Privacy Policy URL"
            value={draft.privacyUrl}
            onChange={handleChange("privacyUrl")}
            fullWidth size="small"
          />
        </DialogContent>
        {saveError && (
          <Box sx={{ px: 3, pt: 1, color: "#b91c1c", fontSize: "0.82rem" }}>
            {saveError}
          </Box>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditClose} disabled={saving} sx={{ color: "#6b7280" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || saved}
            variant="contained"
            sx={{
              backgroundColor: saved ? "#1a7a4a" : "#0d1b4c",
              "&:hover": { backgroundColor: saved ? "#1a7a4a" : "#1a2d5a" },
              "&.Mui-disabled": { backgroundColor: saved ? "#1a7a4a" : undefined, color: saved ? "#fff" : undefined },
              fontFamily: INTER_FONT,
            }}
          >
            {saved ? "Saved!" : saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
